import { CheckCircle2, ChevronRight, Globe2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { priorityActions } from "@/lib/content";
import { cn } from "@/lib/utils";

const priorityStyles = {
  HIGH: "bg-warning/15 text-warning border-warning/30",
  MED: "bg-brand-100 text-brand-800 border-brand-200",
} as const;

interface ReadinessCardProps {
  className?: string;
  compact?: boolean;
}

/** Illustrative product mockup only; no values shown here are live customer data. */
export function ReadinessCard({ className, compact = false }: ReadinessCardProps) {
  const actions = compact ? priorityActions.slice(0, 2) : priorityActions;

  return (
    <div
      role="img"
      aria-label="Illustrative DataProtection.work dashboard showing Philippines DPA and GDPR modules, sample readiness scores, and priority actions."
      className={cn(
        "w-full min-w-0 overflow-hidden rounded-2xl border border-border bg-card shadow-elevated",
        className,
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-secondary/40 px-5 py-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Your privacy frameworks
        </span>
        <Badge variant="outline">Illustrative example</Badge>
      </div>

      <div className="space-y-5 p-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-border bg-background p-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-success" aria-hidden="true" />
              <span className="text-sm font-semibold text-foreground">Philippines DPA</span>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
              Active in this example because the business operates in the Philippines.
            </p>
            <p className="mt-3 text-2xl font-bold text-foreground">
              68<span className="text-sm font-medium text-muted-foreground"> / 100</span>
            </p>
          </div>

          <div className="rounded-xl border border-border bg-background p-4">
            <div className="flex items-center gap-2">
              <Globe2 className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <span className="text-sm font-semibold text-foreground">GDPR / EU</span>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
              Active in this example based on EU/EEA offering or monitoring answers.
            </p>
            <p className="mt-3 text-2xl font-bold text-foreground">
              61<span className="text-sm font-medium text-muted-foreground"> / 100</span>
            </p>
          </div>
        </div>

        <div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-semibold text-foreground">Priority actions</span>
            <span className="text-xs text-muted-foreground">3 in this example</span>
          </div>
          <ul className="mt-3 space-y-2.5">
            {actions.map((action) => (
              <li
                key={action.title}
                className="flex min-w-0 items-start gap-3 rounded-lg border border-border bg-background px-3 py-2.5"
              >
                <span
                  className={cn(
                    "mt-0.5 shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold tracking-wide",
                    priorityStyles[action.priority],
                  )}
                >
                  {action.priority}
                </span>
                <span className="min-w-0 flex-1 text-sm font-medium leading-snug text-foreground">
                  {action.title}
                </span>
                <ChevronRight
                  className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
              </li>
            ))}
          </ul>
        </div>

        {!compact && (
          <div className="rounded-lg bg-accent px-3 py-2.5 text-sm leading-relaxed text-accent-foreground">
            Framework activation and readiness results are guidance based on factual onboarding
            answers and versioned rules, not a legal compliance determination.
          </div>
        )}
      </div>
    </div>
  );
}
